import dynamic from "next/dynamic";
import React, { useState } from "react";

import AddItemSnackbar from "@/components/ui/collection/add-item-snackbar";
import NewIn from "@/components/ui/new-in/index";
import { Search_Item_FieldsFragment } from "@/graphql/types/graphql";
import useCollection from "@/hooks/useCollection";
import useMe from "@/hooks/useMe";
import useSnackbar from "@/hooks/useSnackbar";
import useTracker from "@/hooks/useTracker";

const ContextMenu = dynamic(
  () => import("@/components/core/context-menu/menu"),
  { ssr: false }
);

const ContextMenuItem = dynamic(
  () => import("@/components/core/context-menu/menu-item"),
  { ssr: false }
);

export interface ProductListingProps {
  item: Search_Item_FieldsFragment & { images?: string[] };
  title?: string;
  index?: number;
}

const ProductListing: React.FC<ProductListingProps> = ({
  item,
  title,
  index,
}) => {
  const {
    id,
    name,
    brand,
    retailer,
    price,
    originalPrice,
    thumbnailImage,
    saved,
    newIn,
    images,
    seoURL,
  } = item;
  const { me, goLogin, fetchMe } = useMe();
  const { setShowDrawer, setItemState, unsaveItem, saveItem } = useCollection();
  const { showSnackbar, closeSnackbar } = useSnackbar();
  const [isTracked, setIsTracked] = React.useState(false);
  const {
    productThumbnailViewEvent,
    productClickEvent,
    saveInternalOriginLocal,
  } = useTracker();
  const observerRef = React.useRef<any>(null);
  const ref = React.useRef(null);

  function observeElement(
    element: HTMLElement,
    callback: (isVisible: boolean) => void
  ) {
    if (observerRef && observerRef.current) {
      observerRef.current.disconnect();
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        // `isIntersecting` is true when element is in the viewport
        if (!isTracked) callback(entry.isIntersecting);
      },
      { threshold: 0.8 } // Adjust threshold as needed
    );

    observer.observe(element);
    observerRef.current = observer;

    // Optionally, return the observer so you can disconnect it later if needed
    return observer;
  }

  const [hasSaved, setSaved] = useState(saved);

  const handleSave: React.MouseEventHandler<HTMLImageElement> = async (e) => {
    e.stopPropagation();
    e.nativeEvent.stopPropagation();
    if (!me) {
      goLogin();
      return;
    }

    if (!hasSaved) {
      saveItem({
        item,
        onSuccess: () => {
          setSaved(true);
          fetchMe();
          showSnackbar(
            <AddItemSnackbar handleAddToCollection={handleAddToCollection} />
          );
        },
      });
    } else {
      unsaveItem({
        item,
        onSuccess: () => {
          setSaved(false);
          fetchMe();
        },
      });
    }
  };

  const handleAddToCollection = () => {
    closeSnackbar();
    setShowDrawer(true);
    setItemState(item);
  };

  const observeCallback = (isVisible: boolean) => {
    if (isVisible && !isTracked) {
      setIsTracked(true);
      productThumbnailViewEvent({
        position: index || 0,
        product_id: id,
        product_name: name,
        brand,
        retailer,
        price,
        original_price: originalPrice || 0,
        seo_url: seoURL,
        newIn,
        section_title: title,
      });
    }
  };

  const handleOpenInNew = (isNewWindow?: boolean) => {
    productClickEvent({
      position: index || 0,
      product_id: item.id,
      product_name: item.name,
      brand: item.brand,
      retailer: item.retailer,
      price: item.price,
      original_price: item.originalPrice || 0,
      seo_url: item.seoURL,
      section_title: title,
    });
    saveInternalOriginLocal();
    window.open(
      `/product/${item.seoURL}`,
      "_blank",
      isNewWindow ? "scrollbars=yes,resizable=yes,noopener,noreferrer" : ""
    );
  };

  return (
    <div className="product-listing product_listing_item" ref={ref}>
      <div style={{ overflow: "hidden" }} className="item_img_over">
        {newIn && (
          <>
            <div className="new-in-wrapper">
              <NewIn />
            </div>
            <div className="new-in-wrapper new-in-mobile">
              <NewIn size="small" />
            </div>
          </>
        )}
        <img
          src={`${thumbnailImage || (images && images[0])}`}
          loading="lazy"
          style={{
            objectFit: "cover",
            objectPosition: "bottom",
            height: "100%",
            width: "100%",
            backgroundColor: "#f8f8f9e0",
            aspectRatio: 5 / 8,
          }}
          ref={(node) => {
            if (node) {
              observeElement(node, observeCallback);
            }
          }}
          alt={`${item.brand} ${item.name}`}
        />
        <div className="save-icon-wrapper" onClick={handleSave}>
          <img
            src={hasSaved ? "/heart-fill.svg" : "/heart-line.svg"}
            className="save-icon"
            alt="save"
          />
        </div>
      </div>
      <p className="brand-price truncate">{brand}</p>
      <p className="goods-name-text line-clamp-2">{name}</p>
      <div className="price-section">
        <p
          className={`goods_item_price ${
            originalPrice && originalPrice !== price && "price-red"
          }`}
        >
          ${price.toLocaleString()}
        </p>
        {originalPrice && originalPrice !== price && (
          <p className="goods_item_original_price">
            ${originalPrice.toLocaleString()}
          </p>
        )}
      </div>
      <p className="retailer_style">{retailer}</p>

      <ContextMenu triggerRef={ref}>
        <ContextMenuItem onClick={() => handleOpenInNew()}>
          Open Link in New Tab
        </ContextMenuItem>
      </ContextMenu>

      <style jsx>
        {`
          .product_listing_item {
            width: 100%;
            color: #191919;
          }
          .item_img_over {
            position: relative;
            width: 100%;
          }
          .brand-price {
            font-size: 16px;
            font-weight: 700;
            line-height: normal;
            color: #191919;
            margin-top: 16px;
            letter-spacing: -0.32px;
          }
          .goods-name-text {
            font-size: 14px;
            font-weight: 500;
            line-height: normal;
            color: #666666;
            letter-spacing: -0.28px;
            margin-top: 4px;
          }
          .price-section {
            display: flex;
            align-items: center;
            margin-top: 16px;
          }
          .goods_item_price {
            font-size: 16px;
            font-weight: 700;
            line-height: normal;
            color: #191919;
            letter-spacing: -0.32px;

            &.price-red {
              color: #a20000;
            }
          }
          .goods_item_original_price {
            color: #666;
            text-align: center;
            font-size: 16px;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
            letter-spacing: -0.32px;
            text-decoration: line-through;
            margin-left: 8px;
          }
          .retailer_style {
            font-size: 14px;
            font-weight: 500;
            color: #999;
            margin-top: 12px;
            letter-spacing: -0.28px;
          }

          .save-icon-wrapper {
            position: absolute;
            top: 8px;
            right: 8px;
            padding: 8px;
          }
          .add-item-snackbar {
            display: flex;
            align-items: center;

            .snackbar-right {
              display: flex;
              align-items: center;
            }
          }
          .new-in-wrapper {
            position: absolute;
            left: 12px;
            top: 12px;
          }
          .new-in-mobile {
            display: none;
          }
          @media (max-width: 768px) {
            .product_listing_item {
              width: 100%;
            }
            .item_img_over {
              width: 100%;
            }
            .goods-name-text {
              margin-top: 2px;
            }
            .price-section {
              margin-top: 10px;
            }
            .retailer_style {
              margin-top: 8px;
            }
            .item_img_over {
              margin-top: 0;
            }
            .new-in-wrapper {
              display: none;
            }
            .new-in-mobile {
              display: block;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ProductListing;
